import BigNumber from 'bignumber.js'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { SLOW_INTERVAL } from 'config/constants'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'state'
import useSWRImmutable from 'swr/immutable'
import bondsConfig from 'config/constants/bonds'
import { BIG_ZERO } from 'utils/bigNumber'
import { useCakeBusdPrice } from 'hooks/useBUSDPrice'
import { useWeb3React } from '@web3-react/core'
import { ChainId } from '@pancakeswap/sdk'
import { bondFromNameSelector, capitalSelector, makeBondFromIdSelector, makeUserBondFromIdSelector } from './selectors'
import { fetchCapitalPublicDataAsync, fetchCapitalUserDataAsync } from '.'
import { SerializedBond, SerializedBondUserData, SerializedCapitalState } from './types'

export const usePollBondsData = () => {
  const dispatch = useAppDispatch()
  const { chainId } = useWeb3React()
  useSWRImmutable(
    chainId ? ['publicBondData', chainId] : ['publicBondData', ChainId.BSC],
    async () => {
      const ids = bondsConfig.map((bondToFetch) => bondToFetch.id)
      dispatch(fetchCapitalPublicDataAsync({ ids, chainId: chainId ?? (ChainId.BSC as number) }))
    },
    {
      refreshInterval: SLOW_INTERVAL,
    },
  )
}

export const usePollBondsWithUserData = () => {
  const dispatch = useAppDispatch()
  const { account, chainId } = useActiveWeb3React()
  const name = ['bondsWithUserData', account, chainId]

  useSWRImmutable(
    account && chainId ? name : null,
    async () => {
      const ids = bondsConfig.map((bondToFetch) => bondToFetch.id)
      const params = { account, ids, chainId }
      dispatch(fetchCapitalUserDataAsync(params))
    },
    {
      refreshInterval: SLOW_INTERVAL,
    },
  )
}

export const useCapital = (): SerializedCapitalState => {
  const { chainId } = useActiveWeb3React()
  return useSelector(useMemo(() => capitalSelector(chainId), [chainId]))
}

export const useBondFromId = (id: number): SerializedBond => {
  const bondFromPid = useMemo(() => makeBondFromIdSelector(id), [id])
  return useSelector(bondFromPid)
}

export const useBondFromName = (name: string): SerializedBond => {
  const bondFromName = useMemo(() => bondFromNameSelector(name), [name])
  return useSelector(bondFromName)
}

export const useBondUser = (id): SerializedBondUserData => {
  const bondFromIdUser = useMemo(() => makeUserBondFromIdSelector(id), [id])
  return useSelector(bondFromIdUser)
}

export const useVNTRUSDTPrice = (): BigNumber => {
  const price = useCakeBusdPrice()
  return useMemo(() => (price ? new BigNumber(price.toSignificant(6)) : BIG_ZERO), [price])
}
